import { useContext } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LanguageContext } from "../App";
import { IMAGES } from "../translations";

export default function Breakfast() {
  const { t } = useContext(LanguageContext);
  const b = t.breakfast;
  const photos = IMAGES.breakfast; // 8 photos

  return (
    <section
      id="breakfast"
      data-testid="breakfast-section"
      className="bg-[#F5F1EA] py-24 md:py-32 px-6 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-14 text-center max-w-3xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
            className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-[#7B907F] mb-5"
            data-testid="breakfast-overline"
          >
            {b.overline}
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-['Cormorant_Garamond',serif] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-[#2A2726] tracking-tight mb-8"
            data-testid="breakfast-headline"
          >
            {b.headline}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, delay: 0.2 }}
            className="font-['Cormorant_Garamond',serif] text-xl md:text-2xl font-light text-[#5C5754] leading-relaxed italic"
            data-testid="breakfast-quote"
          >
            "{b.quote}"
          </motion.p>

          {/* Tags */}
          <motion.div
            initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, delay: 0.35 }}
            className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 mt-8"
          >
            {[b.tag1, b.tag2, b.tag3].map((tag, i) => (
              <span key={i} className="flex items-center gap-2 font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] text-[#7B907F]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#7B907F] inline-block" />
                {tag}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Editorial photo grid — 8 photos */}
        <div className="space-y-3 md:space-y-4">

          {/* Row 1: 2 wide shots */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {[photos[0], photos[1]].map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, delay: i * 0.1 }}
                className="overflow-hidden"
                data-testid={`breakfast-img-${i}`}
              >
                <img src={src} alt={b.overline} loading="lazy"
                  className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700 ease-out" />
              </motion.div>
            ))}
          </div>

          {/* Row 2: 3 close-ups */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {[photos[2], photos[3], photos[4]].map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, delay: i * 0.1 }}
                className="overflow-hidden"
                data-testid={`breakfast-img-${i + 2}`}
              >
                <img src={src} alt={b.overline} loading="lazy"
                  className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-700 ease-out" />
              </motion.div>
            ))}
          </div>

          {/* Row 3: fruit + beverages + buffet angle */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {[photos[5], photos[6], photos[7]].map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, delay: i * 0.1 }}
                className="overflow-hidden"
                data-testid={`breakfast-img-${i + 5}`}
              >
                <img src={src} alt={b.overline} loading="lazy"
                  className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-700 ease-out" />
              </motion.div>
            ))}
          </div>

        </div>
        {/* CTA → /prenota */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-14 text-center"
        >
          <Link
            to="/prenota"
            data-testid="breakfast-cta"
            className="inline-flex items-center justify-center bg-[#7B907F] text-white font-['Manrope',sans-serif] text-xs uppercase tracking-[0.25em] w-full sm:w-auto px-10 py-4 hover:bg-[#687A6C] transition-all duration-300"
          >
            {b.cta}
          </Link>
        </motion.div>

      </div>
    </section>
  );
}
